/**
 * Typography tokens following Apple's design principles with Nothing Technology's monospace elements
 * Provides a comprehensive type system with optimal readability and hierarchy
 */

export const typographyTokens = {
  fontFamily: {
    // Apple-inspired system fonts
    sans: [
      '-apple-system',
      'BlinkMacSystemFont',
      'SF Pro Display',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ],
    // Nothing-inspired monospace
    mono: [
      'SF Mono',
      'Monaco',
      'Inconsolata',
      'Roboto Mono',
      'Courier New',
      'monospace',
    ],
    // Alias for mono
    monospace: [
      'SF Mono',
      'Monaco',
      'Inconsolata',
      'Roboto Mono',
      'Courier New',
      'monospace',
    ],
    // Elegant serif for special content
    serif: ['SF Pro Text', 'New York', 'Georgia', 'Times New Roman', 'serif'],
  },

  fontSize: {
    // Apple's type scale with responsive line heights and optimal spacing
    xs: {
      size: 12,
      lineHeight: 16,
      letterSpacing: 0.4,
      paragraphSpacing: 8,
    },
    sm: {
      size: 14,
      lineHeight: 20,
      letterSpacing: 0.2,
      paragraphSpacing: 10,
    },
    md: {
      size: 16,
      lineHeight: 24,
      letterSpacing: 0,
      paragraphSpacing: 12,
    },
    base: {
      size: 16,
      lineHeight: 24,
      letterSpacing: 0,
      paragraphSpacing: 12,
    },
    lg: {
      size: 18,
      lineHeight: 28,
      letterSpacing: -0.2,
      paragraphSpacing: 14,
    },
    xl: {
      size: 20,
      lineHeight: 28,
      letterSpacing: -0.4,
      paragraphSpacing: 16,
    },
    '2xl': {
      size: 24,
      lineHeight: 32,
      letterSpacing: -0.6,
      paragraphSpacing: 18,
    },
    '3xl': {
      size: 30,
      lineHeight: 36,
      letterSpacing: -0.8,
      paragraphSpacing: 20,
    },
    '4xl': {
      size: 36,
      lineHeight: 44,
      letterSpacing: -1.0,
      paragraphSpacing: 24,
    },
    '5xl': {
      size: 48,
      lineHeight: 58,
      letterSpacing: -1.2,
      paragraphSpacing: 28,
    },
    '6xl': {
      size: 60,
      lineHeight: 72,
      letterSpacing: -1.4,
      paragraphSpacing: 32,
    },
  },

  fontWeight: {
    thin: '100' as const,
    extralight: '200' as const,
    light: '300' as const,
    normal: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
    extrabold: '800' as const,
    black: '900' as const,
  },

  letterSpacing: {
    tighter: -0.8,
    tight: -0.4,
    normal: 0,
    wide: 0.4,
    wider: 0.8,
    widest: 1.6,
  },

  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },

  // Typography variants for different content types
  variants: {
    // Display text - for hero sections and large headings
    display: {
      fontFamily: 'sans',
      fontSize: '5xl',
      fontWeight: 'bold',
      letterSpacing: 'tighter',
      lineHeight: 'tight',
    },
    // Headings - hierarchical content structure
    h1: {
      fontFamily: 'sans',
      fontSize: '4xl',
      fontWeight: 'bold',
      letterSpacing: 'tight',
      lineHeight: 'tight',
    },
    h2: {
      fontFamily: 'sans',
      fontSize: '3xl',
      fontWeight: 'semibold',
      letterSpacing: 'tight',
      lineHeight: 'snug',
    },
    h3: {
      fontFamily: 'sans',
      fontSize: '2xl',
      fontWeight: 'semibold',
      letterSpacing: 'normal',
      lineHeight: 'snug',
    },
    h4: {
      fontFamily: 'sans',
      fontSize: 'xl',
      fontWeight: 'medium',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    h5: {
      fontFamily: 'sans',
      fontSize: 'lg',
      fontWeight: 'medium',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    h6: {
      fontFamily: 'sans',
      fontSize: 'base',
      fontWeight: 'medium',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    // Body text variants
    body: {
      fontFamily: 'sans',
      fontSize: 'base',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'relaxed',
    },
    bodyLarge: {
      fontFamily: 'sans',
      fontSize: 'lg',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'relaxed',
    },
    bodySmall: {
      fontFamily: 'sans',
      fontSize: 'sm',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    // UI text variants
    caption: {
      fontFamily: 'sans',
      fontSize: 'xs',
      fontWeight: 'normal',
      letterSpacing: 'wide',
      lineHeight: 'normal',
    },
    label: {
      fontFamily: 'sans',
      fontSize: 'sm',
      fontWeight: 'medium',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    button: {
      fontFamily: 'sans',
      fontSize: 'base',
      fontWeight: 'semibold',
      letterSpacing: 'normal',
      lineHeight: 'none',
    },
    // Heading variant
    heading: {
      fontFamily: 'sans',
      fontSize: 'xl',
      fontWeight: 'semibold',
      letterSpacing: 'tight',
      lineHeight: 'tight',
    },
    // Monospace variants for technical content
    code: {
      fontFamily: 'mono',
      fontSize: 'sm',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    codeBlock: {
      fontFamily: 'mono',
      fontSize: 'sm',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'relaxed',
    },
    // Recipe-specific variants
    recipeTitle: {
      fontFamily: 'sans',
      fontSize: '2xl',
      fontWeight: 'bold',
      letterSpacing: 'tight',
      lineHeight: 'tight',
    },
    recipeStep: {
      fontFamily: 'sans',
      fontSize: 'base',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'relaxed',
    },
    recipeIngredient: {
      fontFamily: 'sans',
      fontSize: 'sm',
      fontWeight: 'normal',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
    recipeMeasurement: {
      fontFamily: 'mono',
      fontSize: 'sm',
      fontWeight: 'medium',
      letterSpacing: 'normal',
      lineHeight: 'normal',
    },
  },

  // Responsive scaling factors for different screen sizes
  scaling: {
    mobile: {
      xs: 0.9,
      sm: 0.95,
      md: 1,
      base: 1,
      lg: 1.05,
      xl: 1.1,
      '2xl': 1.15,
      '3xl': 1.2,
      '4xl': 1.25,
      '5xl': 1.3,
      '6xl': 1.35,
    },
    tablet: {
      xs: 1,
      sm: 1,
      md: 1,
      base: 1,
      lg: 1,
      xl: 1,
      '2xl': 1.1,
      '3xl': 1.15,
      '4xl': 1.2,
      '5xl': 1.25,
      '6xl': 1.3,
    },
    desktop: {
      xs: 1,
      sm: 1,
      md: 1,
      base: 1,
      lg: 1,
      xl: 1,
      '2xl': 1,
      '3xl': 1,
      '4xl': 1,
      '5xl': 1,
      '6xl': 1,
    },
  },

  // Accessibility scaling multipliers
  accessibility: {
    textScale: {
      xs: 0.82,
      sm: 0.89,
      base: 1,
      lg: 1.12,
      xl: 1.23,
      xxl: 1.35,
      xxxl: 1.48,
    },
  },
} as const;

// Type definitions for typography tokens
export type FontFamily = keyof typeof typographyTokens.fontFamily;
export type FontSize = keyof typeof typographyTokens.fontSize;
export type FontWeight = keyof typeof typographyTokens.fontWeight;
export type LetterSpacing = keyof typeof typographyTokens.letterSpacing;
export type LineHeight = keyof typeof typographyTokens.lineHeight;
export type TypographyVariant = keyof typeof typographyTokens.variants;
export type ScalingDevice = keyof typeof typographyTokens.scaling;
export type AccessibilityTextScale =
  keyof typeof typographyTokens.accessibility.textScale;

// Utility type for font configuration
export interface FontConfig {
  fontFamily: FontFamily;
  fontSize: FontSize;
  fontWeight?: FontWeight;
  letterSpacing?: LetterSpacing;
  lineHeight?: LineHeight;
}
